import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserCheck, UserPlus, Loader2, Home, Briefcase, MapPin, Plus, Trash2, Pencil, Star, X } from "lucide-react";

const ADDRESS_PRESETS = [
  { key: "Ev", label: "Ev", icon: Home },
  { key: "İş", label: "İş", icon: Briefcase },
  { key: "Yurt", label: "Yurt", icon: MapPin },
  { key: "Diğer", label: "Diğer", icon: MapPin },
];

function AddressIcon({ label }) {
  const p = ADDRESS_PRESETS.find((a) => a.key === label);
  const I = p?.icon || MapPin;
  return <I className="w-4 h-4" />;
}

// Initial mode: if we already know a customer_number in stored profile => new order flow,
// otherwise show the choice between "returning customer" and "new customer".
export default function ProfileDialog({ open, onOpenChange, onSaved }) {
  const { t, profile, setProfile } = useApp();
  const [mode, setMode] = useState("choose"); // choose | returning | form | addresses
  const [lookup, setLookup] = useState({ customer_number: "", phone: "" });
  const [looking, setLooking] = useState(false);
  const [addresses, setAddresses] = useState([]); // for returning customers
  const [editingAddr, setEditingAddr] = useState(null); // address being edited (or null for new)
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    neighborhood: "",
    street: "",
    building: "",
    floor: "",
    apt: "",
    address_id: null,
    address_label: "",
    customer_number: null,
  });

  useEffect(() => {
    if (!open) return;
    if (profile) {
      setForm({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        phone: profile.phone || "",
        neighborhood: profile.neighborhood || "",
        street: profile.street || "",
        building: profile.building || "",
        floor: profile.floor || "",
        apt: profile.apt || "",
        address_id: profile.address_id || null,
        address_label: profile.address_label || "",
        customer_number: profile.customer_number || null,
      });
      setMode(profile.customer_number ? "form" : "form");
      // Load addresses in background for known customers
      if (profile.customer_number && profile.phone) {
        refreshAddresses(profile.customer_number, profile.phone);
      }
    } else {
      setMode("choose");
      setAddresses([]);
    }
  }, [profile, open]);

  const refreshAddresses = async (cn, ph) => {
    try {
      const { data } = await api.get(`/customers/${cn}/addresses`, { params: { phone: ph } });
      setAddresses(data.addresses || []);
    } catch {
      setAddresses([]);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyAddress = (a) => {
    setForm((f) => ({
      ...f,
      address_id: a.id,
      address_label: a.label || "",
      neighborhood: a.neighborhood || "",
      street: a.street || "",
      building: a.building || "",
      floor: a.floor || "",
      apt: a.apt || "",
    }));
  };

  const doLookup = async () => {
    const num = parseInt(lookup.customer_number);
    if (!num || !lookup.phone.trim()) {
      toast.error(t("lookup_missing"));
      return;
    }
    setLooking(true);
    try {
      const { data } = await api.post("/customers/lookup", {
        customer_number: num,
        phone: lookup.phone.trim(),
      });
      const addrs = data.addresses || [];
      const def = addrs.find((a) => a.is_default) || addrs[0] || {};
      setForm({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        phone: data.phone || lookup.phone,
        neighborhood: def.neighborhood || "",
        street: def.street || "",
        building: def.building || "",
        floor: def.floor || "",
        apt: def.apt || "",
        address_id: def.id || null,
        address_label: def.label || "",
        customer_number: data.customer_number,
      });
      setAddresses(addrs);
      toast.success(t("welcome_back", { name: data.first_name || "" }));
      // If customer has >1 addresses, show the picker first
      setMode(addrs.length > 1 ? "addresses" : "form");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("lookup_failed"));
    } finally {
      setLooking(false);
    }
  };

  const saveAddress = async (data) => {
    if (!form.customer_number || !form.phone) {
      toast.error("Önce müşteri numarası gerekli");
      return;
    }
    try {
      let res;
      if (editingAddr?.id) {
        res = await api.patch(`/customers/${form.customer_number}/addresses/${editingAddr.id}`, {
          phone: form.phone,
          ...data,
        });
      } else {
        res = await api.post(`/customers/${form.customer_number}/addresses`, {
          phone: form.phone,
          ...data,
        });
      }
      setAddresses(res.data.addresses || []);
      // If a new address was created, apply it immediately
      if (res.data.address) applyAddress(res.data.address);
      toast.success(editingAddr?.id ? "Adres güncellendi" : "Adres eklendi");
      setEditingAddr(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const removeAddress = async (a) => {
    if (!window.confirm(`"${a.label || "Adres"}" silinsin mi?`)) return;
    try {
      const r = await api.delete(`/customers/${form.customer_number}/addresses/${a.id}`, {
        params: { phone: form.phone },
      });
      setAddresses(r.data.addresses || []);
      toast.success("Adres silindi");
      if (form.address_id === a.id) {
        const next = (r.data.addresses || []).find((x) => x.is_default) || (r.data.addresses || [])[0];
        if (next) applyAddress(next);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const setDefault = async (a) => {
    try {
      const r = await api.patch(`/customers/${form.customer_number}/addresses/${a.id}`, {
        phone: form.phone,
        is_default: true,
      });
      setAddresses(r.data.addresses || []);
      toast.success(`"${a.label || "Adres"}" varsayılan yapıldı`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const save = () => {
    const required = [
      { k: "first_name", label: t("first_name") },
      { k: "last_name", label: t("last_name") },
      { k: "phone", label: t("phone") },
      { k: "neighborhood", label: t("neighborhood") },
      { k: "street", label: t("street") },
    ];
    const missing = required
      .filter((r) => !String(form[r.k] ?? "").trim())
      .map((r) => r.label);
    if (missing.length > 0) {
      toast.error(`Lütfen bu alanları doldurun: ${missing.join(", ")}`);
      return;
    }
    const clean = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
    );
    setProfile(clean);
    toast.success("Bilgileriniz kaydedildi");
    onOpenChange(false);
    if (onSaved) onSaved(clean);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="profile-dialog"
        className="bg-[#FAF7F2] border-[#E8E2D5] max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{t("profile_title")}</DialogTitle>
          <DialogDescription>{t("profile_desc")}</DialogDescription>
        </DialogHeader>

        {mode === "choose" && (
          <div className="grid gap-3">
            <button
              data-testid="mode-returning"
              onClick={() => setMode("returning")}
              className="text-left bg-white hover:bg-[#2E5A36] hover:text-[#FAF7F2] border border-[#E8E2D5] rounded-2xl p-4 flex items-center gap-3 transition-all group"
            >
              <div className="w-12 h-12 rounded-full bg-[#2E5A36]/10 group-hover:bg-[#FAF7F2]/20 flex items-center justify-center">
                <UserCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="font-heading font-bold">{t("returning_customer")}</p>
                <p className="text-xs opacity-70">{t("returning_customer_desc")}</p>
              </div>
            </button>
            <button
              data-testid="mode-new"
              onClick={() => setMode("form")}
              className="text-left bg-white hover:bg-[#D9653B] hover:text-white border border-[#E8E2D5] rounded-2xl p-4 flex items-center gap-3 transition-all group"
            >
              <div className="w-12 h-12 rounded-full bg-[#D9653B]/10 group-hover:bg-white/20 flex items-center justify-center">
                <UserPlus className="w-6 h-6" />
              </div>
              <div>
                <p className="font-heading font-bold">{t("new_customer")}</p>
                <p className="text-xs opacity-70">{t("new_customer_desc")}</p>
              </div>
            </button>
          </div>
        )}

        {mode === "returning" && (
          <div className="grid gap-3">
            <div>
              <Label>{t("customer_number_label")} *</Label>
              <Input
                data-testid="lookup-customer-number"
                inputMode="numeric"
                value={lookup.customer_number}
                onChange={(e) => setLookup((l) => ({ ...l, customer_number: e.target.value }))}
                className="bg-white"
                placeholder="1001"
              />
            </div>
            <div>
              <Label>{t("phone")} *</Label>
              <Input
                data-testid="lookup-phone"
                value={lookup.phone}
                onChange={(e) => setLookup((l) => ({ ...l, phone: e.target.value }))}
                className="bg-white"
                placeholder="+90 533 ..."
              />
            </div>
            <p className="text-xs text-[#1B2A22]/60">{t("lookup_hint")}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("choose")} className="flex-1 rounded-full">
                {t("back")}
              </Button>
              <Button
                data-testid="lookup-submit"
                onClick={doLookup}
                disabled={looking}
                className="flex-1 bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full"
              >
                {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : t("continue")}
              </Button>
            </div>
          </div>
        )}

        {mode === "addresses" && (
          <AddressPicker
            addresses={addresses}
            activeId={form.address_id}
            onPick={(a) => { applyAddress(a); setMode("form"); }}
            onAddNew={() => { setEditingAddr({}); setMode("form"); }}
            onEdit={(a) => { setEditingAddr(a); setMode("form"); applyAddress(a); }}
            onRemove={removeAddress}
            onSetDefault={setDefault}
            onContinue={() => setMode("form")}
          />
        )}

        {mode === "form" && (
          <>
            {form.customer_number ? (
              <div
                data-testid="profile-customer-number"
                className="bg-[#2E5A36]/10 border border-[#2E5A36]/30 rounded-xl p-3 flex items-center justify-between"
              >
                <div>
                  <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">
                    {t("customer_number_label")}
                  </p>
                  <p className="font-mono font-extrabold text-2xl text-[#1B2A22]">
                    #{String(form.customer_number).padStart(4, "0")}
                  </p>
                </div>
                <UserCheck className="w-8 h-8 text-[#2E5A36]" />
              </div>
            ) : (
              <p className="text-xs text-[#1B2A22]/60 -mt-2">{t("new_customer_hint")}</p>
            )}

            {/* Multi-address summary — only for known customers with saved addresses */}
            {form.customer_number && addresses.length > 0 && (
              <div className="bg-white border border-[#E8E2D5] rounded-xl p-3 space-y-2" data-testid="addr-summary">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Kayıtlı Adreslerim ({addresses.length})</p>
                  <button
                    data-testid="addr-manage-btn"
                    type="button"
                    onClick={() => setMode("addresses")}
                    className="text-xs font-heading font-bold text-[#D9653B] hover:underline"
                  >
                    Yönet →
                  </button>
                </div>
                <div className="flex overflow-x-auto gap-2 -mx-1 px-1 pb-1">
                  {addresses.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`addr-chip-${a.id}`}
                      onClick={() => applyAddress(a)}
                      className={`shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-heading font-bold transition ${
                        form.address_id === a.id
                          ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]"
                          : "bg-white text-[#1B2A22] border-[#E8E2D5] hover:border-[#2E5A36]"
                      }`}
                    >
                      <AddressIcon label={a.label} />
                      {a.label || "Adres"}
                      {a.is_default && <Star className="w-3 h-3 text-[#E5A024] fill-[#E5A024]" />}
                    </button>
                  ))}
                  <button
                    data-testid="addr-new-chip"
                    type="button"
                    onClick={() => {
                      setEditingAddr({});
                      setForm((f) => ({ ...f, address_id: null, address_label: "", neighborhood: "", street: "", building: "", floor: "", apt: "" }));
                    }}
                    className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-[#2E5A36] text-[#2E5A36] text-xs font-heading font-bold"
                  >
                    <Plus className="w-3 h-3" /> Yeni Adres
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("first_name")} *</Label>
                <Input data-testid="profile-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-white" />
              </div>
              <div>
                <Label>{t("last_name")} *</Label>
                <Input data-testid="profile-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-white" />
              </div>
              <div className="col-span-2">
                <Label>{t("phone")} *</Label>
                <Input data-testid="profile-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white" placeholder="+90 533 ..." />
              </div>

              {/* Address label picker */}
              <div className="col-span-2">
                <Label>Adres Etiketi</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ADDRESS_PRESETS.map((p) => {
                    const I = p.icon;
                    const active = form.address_label === p.key;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        data-testid={`addr-label-${p.key}`}
                        onClick={() => set("address_label", p.key)}
                        className={`inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-heading font-bold border transition ${
                          active ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5] hover:border-[#2E5A36]"
                        }`}
                      >
                        <I className="w-3.5 h-3.5" /> {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="col-span-2">
                <Label>{t("neighborhood")} *</Label>
                <Input data-testid="profile-neighborhood" value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className="bg-white" placeholder="Karadağ" />
              </div>
              <div className="col-span-2">
                <Label>{t("street")} *</Label>
                <Input data-testid="profile-street" value={form.street} onChange={(e) => set("street", e.target.value)} className="bg-white" placeholder="19 Mayıs Caddesi" />
              </div>
              <div className="col-span-2">
                <Label>{t("building")}</Label>
                <Input data-testid="profile-building" value={form.building} onChange={(e) => set("building", e.target.value)} className="bg-white" placeholder="Apartman adı / No" />
              </div>
              <div>
                <Label>{t("floor")}</Label>
                <Input data-testid="profile-floor" value={form.floor} onChange={(e) => set("floor", e.target.value)} className="bg-white" />
              </div>
              <div>
                <Label>{t("apt")}</Label>
                <Input data-testid="profile-apt" value={form.apt} onChange={(e) => set("apt", e.target.value)} className="bg-white" />
              </div>
            </div>

            {/* Save-this-address action (only when logged customer and address content exists) */}
            {form.customer_number && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  data-testid="save-this-addr-btn"
                  variant="outline"
                  onClick={() => saveAddress({
                    label: form.address_label || "Adres",
                    neighborhood: form.neighborhood, street: form.street,
                    building: form.building, floor: form.floor, apt: form.apt,
                  })}
                  className="rounded-full border-[#2E5A36] text-[#2E5A36] text-xs h-9"
                >
                  {editingAddr?.id ? "💾 Bu Adresi Güncelle" : "➕ Bu Adresi Kaydet"}
                </Button>
                {editingAddr && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingAddr(null)}
                    className="rounded-full text-xs h-9"
                  >
                    <X className="w-3 h-3 mr-1" /> İptal
                  </Button>
                )}
              </div>
            )}

            <Button
              data-testid="profile-save"
              onClick={save}
              className="w-full h-12 bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full font-heading font-bold mt-3"
            >
              {t("save_profile")}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddressPicker({ addresses, activeId, onPick, onAddNew, onEdit, onRemove, onSetDefault, onContinue }) {
  return (
    <div className="space-y-3" data-testid="address-picker">
      <p className="text-sm text-[#1B2A22]/70">
        Hangi adrese teslim edelim? Birden çok adresin varsa birini seç veya yenisini ekle.
      </p>
      <div className="grid gap-2">
        {addresses.map((a) => (
          <div
            key={a.id}
            data-testid={`addr-card-${a.id}`}
            className={`bg-white border rounded-2xl p-3 flex items-start gap-3 transition ${
              activeId === a.id ? "border-[#2E5A36] ring-2 ring-[#2E5A36]/30" : "border-[#E8E2D5]"
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-[#2E5A36]/10 flex items-center justify-center shrink-0">
              <AddressIcon label={a.label} />
            </div>
            <button
              type="button"
              onClick={() => onPick(a)}
              className="flex-1 min-w-0 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-[#1B2A22]">{a.label || "Adres"}</span>
                {a.is_default && (
                  <span className="inline-flex items-center gap-1 text-[10px] bg-[#E5A024]/15 text-[#B4711C] font-heading font-bold rounded-full px-2 py-0.5">
                    <Star className="w-3 h-3 fill-[#E5A024] text-[#E5A024]" /> Varsayılan
                  </span>
                )}
              </div>
              <p className="text-sm text-[#1B2A22]/80 mt-1 break-words">{a.formatted || "—"}</p>
            </button>
            <div className="flex flex-col gap-1 shrink-0">
              {!a.is_default && (
                <button
                  type="button"
                  data-testid={`addr-default-${a.id}`}
                  onClick={() => onSetDefault(a)}
                  className="text-[10px] text-[#2E5A36] font-heading font-bold hover:underline"
                >
                  Varsayılan Yap
                </button>
              )}
              <button
                type="button"
                data-testid={`addr-edit-${a.id}`}
                onClick={() => onEdit(a)}
                className="text-[10px] text-[#D9653B] font-heading font-bold hover:underline"
              >
                <Pencil className="w-3 h-3 inline mr-1" /> Düzenle
              </button>
              <button
                type="button"
                data-testid={`addr-remove-${a.id}`}
                onClick={() => onRemove(a)}
                className="text-[10px] text-red-600 font-heading font-bold hover:underline"
              >
                <Trash2 className="w-3 h-3 inline mr-1" /> Sil
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button
          type="button"
          data-testid="addr-add-new-btn"
          variant="outline"
          onClick={onAddNew}
          className="flex-1 rounded-full border-dashed border-[#2E5A36] text-[#2E5A36]"
        >
          <Plus className="w-4 h-4 mr-1" /> Yeni Adres Ekle
        </Button>
        <Button
          type="button"
          data-testid="addr-continue-btn"
          onClick={onContinue}
          className="flex-1 bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold"
        >
          Devam
        </Button>
      </div>
    </div>
  );
}

export function formatAddress(p) {
  if (!p) return "";
  const parts = [
    p.neighborhood && `${p.neighborhood} Mah.`,
    p.street,
    p.building && `${p.building}`,
    p.floor && `Kat: ${p.floor}`,
    p.apt && `Daire: ${p.apt}`,
  ].filter(Boolean);
  return parts.join(", ");
}
